// OfflineIndicator — small fixed banner that fades in when the device is
// offline and fades out when connectivity returns.
//
// Wing operates fine in read-only mode offline (data is served from the
// service-worker cache + pageCache localStorage), so we want a calm signal
// instead of a destructive error. We keep the indicator at the very top of
// the viewport so it doesn't fight the bottom nav.

import { useEffect, useState } from "react";
import { CloudOff, Wifi } from "lucide-react";

export default function OfflineIndicator() {
    const [online, setOnline] = useState(
        typeof navigator !== "undefined" ? navigator.onLine : true,
    );
    // After we're back online we briefly show a green confirmation chip
    // before fading the whole indicator away.
    const [justRecovered, setJustRecovered] = useState(false);

    useEffect(() => {
        const onUp = () => {
            setOnline(true);
            setJustRecovered(true);
            setTimeout(() => setJustRecovered(false), 2200);
        };
        const onDown = () => setOnline(false);
        window.addEventListener("online", onUp);
        window.addEventListener("offline", onDown);
        return () => {
            window.removeEventListener("online", onUp);
            window.removeEventListener("offline", onDown);
        };
    }, []);

    if (online && !justRecovered) return null;

    const isOffline = !online;
    return (
        <div
            data-testid="offline-indicator"
            data-online={online ? "true" : "false"}
            className="fixed top-2 left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
            style={{
                animation: "wing-offline-pop 240ms cubic-bezier(0.22, 1, 0.36, 1) both",
            }}
        >
            <div
                className="flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur"
                style={{
                    background: isOffline
                        ? "rgba(255, 69, 58, 0.18)"
                        : "rgba(48, 209, 88, 0.18)",
                    border: `1px solid ${isOffline ? "rgba(255,69,58,0.45)" : "rgba(48,209,88,0.45)"}`,
                    color: isOffline ? "#FF8A80" : "#A0F1B7",
                    boxShadow: `0 6px 20px ${isOffline ? "rgba(255,69,58,0.18)" : "rgba(48,209,88,0.18)"}`,
                }}
            >
                {isOffline ? (
                    <CloudOff size={13} strokeWidth={2.4} />
                ) : (
                    <Wifi size={13} strokeWidth={2.4} />
                )}
                <span className="text-[11px] font-bold tracking-wide">
                    {isOffline ? "Offline · showing cached data" : "Back online"}
                </span>
            </div>
            <style>{`
                @keyframes wing-offline-pop {
                    0%   { opacity: 0; transform: translate(-50%, -8px); }
                    100% { opacity: 1; transform: translate(-50%, 0); }
                }
            `}</style>
        </div>
    );
}
